import { useCallback } from 'react'
import { useFetchWidget } from '../hooks'
import { resolvePropertyLink, type PropertyLink } from './property-href'
import { usePropertyHrefFeed } from './use-property-href-feed'

/**
 * PDP route resolution + slug navigation, shared by widgets that jump to a
 * property's page (#1142 review). Resolves the feed's reverse-proxy `pdp`
 * route (falling back to `/property/[slug]`) and returns a full-navigation
 * callback (WP parity — a real page load, not SPA routing).
 *
 * Adopted by property-finder first; the other widgets carrying local copies
 * of this resolution (explorer's propertyNav, multi-calendar, booking) move
 * over in a follow-up.
 *
 * #1714: the link itself comes from the shared property-href resolver, so a
 * property the feed maps to its own page (External Property Pages) navigates
 * there. `resolveLink` hands the same answer to callers that build their own
 * URL (the share link).
 */
export function usePdpRoute(feedId: number, widgetId: string): {
  pdpRoute: string
  resolveLink: (slug: string) => PropertyLink
  navigateToProperty: (slug: string) => void
} {
  const { data: widgetData } = useFetchWidget(feedId, widgetId)
  const pdpRoute = widgetData?.feed?.additional_info?.reverseProxy?.routes?.pdp || '/property/[slug]'
  const hrefFeed = usePropertyHrefFeed(widgetData?.feed)
  const resolveLink = useCallback(
    (slug: string) => resolvePropertyLink({ feed: hrefFeed, slug, pdpRoute }),
    [hrefFeed, pdpRoute],
  )
  const navigateToProperty = useCallback((slug: string) => {
    if (typeof window !== 'undefined') window.location.href = resolveLink(slug).href
  }, [resolveLink])
  return { pdpRoute, resolveLink, navigateToProperty }
}
